import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, ArrowRight, GitCompareArrows, AlertTriangle, Loader2, ChevronRight, FileClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";

const STEPS = [
  ["state", "State"], ["setting", "Care Setting"], ["org_type", "Organization Type"],
  ["payer", "Program / Payer"], ["role", "Worker Role"],
];

function PendingBadge() {
  return <span data-testid="verification-pending" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full"><FileClock className="w-3 h-3" /> Verification pending</span>;
}

export default function Compliance() {
  const [tax, setTax] = useState(null);
  const [sel, setSel] = useState({ state: "", setting: "", org_type: "", payer: "", role: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
    api.get("/compliance/updates").then((r) => setUpdates(r.data.updates || [])).catch(() => {});
  }, []);

  const options = useMemo(() => tax ? {
    state: tax.states.map((s) => [s.slug, s.name]),
    setting: tax.care_settings.map((s) => [s.id, s.name]),
    org_type: tax.org_types.map((s) => [s.id, s.name]),
    payer: tax.payer_types.map((s) => [s.id, s.name]),
    role: tax.worker_roles.map((s) => [s.id, s.name]),
  } : {}, [tax]);

  const runNavigator = async () => {
    if (!sel.state) return;
    setLoading(true); setResult(null);
    try {
      const params = new URLSearchParams({ state: sel.state });
      ["setting", "org_type", "payer", "role"].forEach((k) => { if (sel[k]) params.append(k, sel[k]); });
      const { data } = await api.get(`/compliance/requirements?${params.toString()}`);
      setResult(data);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> All 50 States + DC · Federal · State · Local</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-3xl leading-tight">Training &amp; Compliance Navigator</h1>
          <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">Drill into the applicable training, credential, licensing and program requirements for any jurisdiction, care setting and worker role — sourced and verified, never guessed.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="compare-states-cta" className="border-white/25 text-white bg-white/5 hover:bg-white/10"><Link to="/compliance/compare"><GitCompareArrows className="w-4 h-4 mr-2" /> Compare States</Link></Button>
          </div>
        </div>
      </section>

      {/* Navigator */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
          <div className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm h-fit" data-testid="navigator-panel">
            <div className="flex items-center gap-2 mb-5"><MapPin className="w-5 h-5 text-teal-600" /><h2 className="font-display text-xl font-bold text-blue-950">Find applicable requirements</h2></div>
            <div className="space-y-4">
              {STEPS.map(([key, label], i) => (
                <div key={key} className="flex items-start gap-3">
                  <span className="mt-1 grid place-items-center w-6 h-6 rounded-full bg-blue-950 text-white text-xs font-bold shrink-0">{i + 1}</span>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-blue-950">{label}{key !== "state" && <span className="text-slate-400 font-normal"> (optional)</span>}</label>
                    <Select value={sel[key]} onValueChange={(v) => setSel({ ...sel, [key]: v })} disabled={!tax}>
                      <SelectTrigger data-testid={`nav-${key}`} className="mt-1.5"><SelectValue placeholder={`Select ${label.toLowerCase()}…`} /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(options[key] || []).map(([val, name]) => <SelectItem key={val} value={val}>{name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={runNavigator} disabled={!sel.state || loading} data-testid="navigator-submit" className="w-full mt-6 h-12 bg-teal-600 hover:bg-teal-700 text-white">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "View Requirements"}
            </Button>
          </div>

          <div>
            {!result && (
              <div className="h-full grid place-items-center text-center bg-slate-50 rounded-2xl border border-dashed border-blue-900/10 p-10">
                <div><ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Select a state and drill down to see requirements grouped by category.</p></div>
              </div>
            )}
            {result && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="navigator-result">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-bold text-blue-950">{result.state.name} — requirements</h3>
                  <Link to={`/compliance/${result.state.slug}`} className="text-sm text-teal-700 font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all">State profile <ArrowRight className="w-4 h-4" /></Link>
                </div>
                {!result.has_verified_data && (
                  <div className="mb-4 flex items-start gap-2 bg-amber-50 text-amber-900 rounded-xl px-4 py-3 text-sm border border-amber-200">
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <span>Verified requirements for this selection haven't been entered yet. Each item below is marked <strong>verification pending</strong> — the platform never invents a requirement.</span>
                  </div>
                )}
                <div className="space-y-2.5">
                  {result.categories.map((c) => (
                    <div key={c.id} data-testid={`req-cat-${c.id}`} className="bg-white rounded-xl border border-blue-900/5 p-4">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-blue-950 text-sm">{c.name}</p>
                        {c.status === "verified" ? <span className="text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full">{c.requirements.length} verified</span> : <PendingBadge />}
                      </div>
                      {c.status === "verified" && (
                        <ul className="mt-3 space-y-2">
                          {c.requirements.map((r, i) => (
                            <li key={r.id || i} className="text-sm text-slate-600 border-l-2 border-teal-500 pl-3">
                              <p className="text-blue-950">{r.requirement}</p>
                              {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-teal-700 underline">View source</a>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Published regulatory updates */}
      {updates.length > 0 && (
      <section className="bg-slate-50 border-y border-blue-900/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl font-bold text-blue-950 mb-5">Regulatory Updates</h2>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="regulatory-updates">
            {updates.map((u) => (
              <Link key={u.id} to={u.state_slug ? `/compliance/${u.state_slug}` : "/compliance"} className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between"><span className="text-xs font-bold text-teal-700">{u.state_name}</span><span className="text-xs text-slate-400">{u.effective_date}</span></div>
                <p className="font-display font-semibold text-blue-950 mt-2">{u.title}</p>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{u.note}</p>
                <span className="inline-block mt-3 text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full">{u.category_name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Browse all states */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl font-bold text-blue-950 mb-6">Browse state profiles</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5" data-testid="states-grid">
          {tax?.states.map((s) => (
            <Link key={s.slug} to={`/compliance/${s.slug}`} data-testid={`state-link-${s.slug}`}
              className="group flex items-center justify-between bg-white rounded-xl border border-blue-900/5 px-3.5 py-3 hover:border-teal-500 hover:shadow-sm transition-all">
              <span className="text-sm font-medium text-blue-950">{s.name}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 transition-colors" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
